// Reusable Visual Editor for pages that only need an ordered list of sections
// (Home, Glossary intro, Bookmarks intro, Pathway intro). Same split-pane +
// drag-and-drop UX as BlogEditor.jsx but without any blog-specific metadata,
// .docx upload, or scheduling fields. Sections use the same schema and are
// rendered by the shared SectionRenderer so preview stays reader-accurate.
import { createPortal } from "react-dom";
import { useEffect, useRef, useState, Fragment } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectTrigger, SelectContent, SelectItem, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem,
  DropdownMenuLabel, DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import { toast } from "sonner";
import { Plus, Trash2, GripVertical, Eye, EyeOff, X, PencilLine, Undo2, Redo2, ChevronDown, ArrowLeftRight, Maximize2 } from "lucide-react";
import { DragDropContext, Droppable, Draggable } from "@hello-pangea/dnd";
import { Switch } from "@/components/ui/switch";
import { SECTION_GROUPS, SUBTYPE_META, defaultSectionForSubtype } from "@/components/sectionTypes";
import { osBinding, matchesShortcut } from "@/lib/shortcuts";
import SectionRenderer from "@/components/SectionRenderer";
import SectionSubtypeEditor from "@/components/SectionSubtypeEditor";
import EditLockBanner from "@/components/EditLockBanner";
import { useEditLock } from "@/lib/useEditLock";
import { useUnsavedGuard } from "@/lib/useUnsavedGuard";

export default function PageSectionsEditor({
  open, onOpenChange,
  title = "Edit page",
  initialSections = [],
  onSave,
  resourceKey = null,   // "page:home" · "pathway_intro:{id}" — enables concurrent-edit lock
}) {
  const [sections, setSections] = useState(() => structuredClone(initialSections || []));
  const [saving, setSaving] = useState(false);
  const [veSettings, setVeSettings] = useState(null);
  const [editorUi, setEditorUi] = useState({ flip_panes: false, editor_full_pane: false, preview_ratio: 50 });
  const [dirty, setDirty] = useState(false);
  const [confirmClose, setConfirmClose] = useState(false);
  // Guard against leaving the page while there are unsaved edits — intercepts
  // React Router navigation AND browser tab close / reload.
  useUnsavedGuard(open && dirty);
  const [editingSectionId, setEditingSectionId] = useState(null);
  const previewRefs = useRef({});
  const previewPaneRef = useRef(null);
  const sectionEditorRef = useRef(null);
  const historyRef = useRef({ past: [], future: [] });
  const [historyTick, setHistoryTick] = useState(0);
  const pendingDeleteRef = useRef({ toastId: null, count: 0 });
  const [confirmState, setConfirmState] = useState({ open: false, title: "", body: "", onConfirm: null });
  const confirmDelete = (t, b, fn) => setConfirmState({ open: true, title: t, body: b, onConfirm: fn });

  // Concurrent-edit lock — only active while the dialog is open and a
  // resourceKey has been supplied by the parent.
  const lock = useEditLock(resourceKey, { enabled: open && !!resourceKey });
  const readOnly = !!resourceKey && lock.status !== "held";

  const snap = (arr) => JSON.parse(JSON.stringify(arr));
  const pushHistory = (prev) => {
    historyRef.current.past.push(snap(prev));
    if (historyRef.current.past.length > 50) historyRef.current.past.shift();
    historyRef.current.future = [];
    setHistoryTick(t => t + 1);
  };

  // Reset local state whenever the dialog is (re-)opened.
  useEffect(() => {
    if (open) {
      setSections(structuredClone(initialSections || []));
      setDirty(false);
      setEditingSectionId(null);
      historyRef.current = { past: [], future: [] };
      setHistoryTick(t => t + 1);
    }
  }, [open, initialSections]);

  useEffect(() => {
    if (open) api.get("/site-settings/visual-editor").then(r => setVeSettings(r.data)).catch(() => setVeSettings({}));
  }, [open]);

  useEffect(() => {
    if (open) api.get("/site-settings/blog-editor-ui").then(r => setEditorUi(r.data)).catch(() => {});
  }, [open]);
  // Debounced save so slider drags don't spam the backend. Uses the same
  // endpoint as BlogEditor so the preference is shared globally across
  // every Visual Editor in the app.
  const uiSaveTimerRef = useRef(null);
  const setEditorUiField = (patch) => {
    setEditorUi(prev => {
      const next = { ...prev, ...patch };
      if (uiSaveTimerRef.current) clearTimeout(uiSaveTimerRef.current);
      uiSaveTimerRef.current = setTimeout(() => {
        api.put("/site-settings/blog-editor-ui", next).catch(() => {});
      }, 300);
      return next;
    });
  };

  const scrollPreviewTo = (id) => {
    const target = previewRefs.current[id];
    const container = previewPaneRef.current;
    if (target && container) {
      const top = target.getBoundingClientRect().top - container.getBoundingClientRect().top + container.scrollTop - 12;
      container.scrollTo({ top, behavior: "smooth" });
    }
  };
  const openSection = (id) => {
    setEditingSectionId(id);
    setTimeout(() => sectionEditorRef.current?.scrollIntoView({ behavior: "smooth", block: "start" }), 40);
  };

  const undo = () => {
    const { past, future } = historyRef.current;
    if (!past.length) return;
    const prev = past.pop();
    future.push(snap(sections));
    if (future.length > 50) future.shift();
    if (editingSectionId && !prev.some(s => s.id === editingSectionId)) setEditingSectionId(null);
    setSections(prev);
    setDirty(true);
    setHistoryTick(t => t + 1);
    toast("Undid last change");
  };
  const redo = () => {
    const { past, future } = historyRef.current;
    if (!future.length) return;
    const next = future.pop();
    past.push(snap(sections));
    if (past.length > 50) past.shift();
    if (editingSectionId && !next.some(s => s.id === editingSectionId)) setEditingSectionId(null);
    setSections(next);
    setDirty(true);
    setHistoryTick(t => t + 1);
    toast("Redid change");
  };

  useEffect(() => {
    if (!open) return;
    let cancelled = false, cfg = null;
    api.get("/site-settings/keyboard-shortcuts").then(r => { if (!cancelled) cfg = r.data.shortcuts || {}; }).catch(() => {});
    const onKey = (e) => {
      const t = e.target, tag = t?.tagName;
      if (tag === "INPUT" || tag === "TEXTAREA" || t?.isContentEditable) return;
      if (!cfg) return;
      const u = cfg["editor-undo"], r = cfg["editor-redo"];
      if (u?.enabled && matchesShortcut(e, osBinding(u))) { e.preventDefault(); undo(); return; }
      if (r?.enabled && matchesShortcut(e, osBinding(r))) { e.preventDefault(); redo(); return; }
    };
    window.addEventListener("keydown", onKey);
    return () => { cancelled = true; window.removeEventListener("keydown", onKey); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, sections, editingSectionId]);

  useEffect(() => {
    if (!confirmState.open && !confirmClose) return;
    const onKey = (e) => {
      if (e.key === "Escape") {
        e.preventDefault(); e.stopPropagation();
        if (confirmState.open) setConfirmState({ open: false, title: "", body: "", onConfirm: null });
        if (confirmClose) setConfirmClose(false);
      }
    };
    window.addEventListener("keydown", onKey, true);
    return () => window.removeEventListener("keydown", onKey, true);
  }, [confirmState.open, confirmClose]);

  const updateSection = (idx, k, v) => {
    if (k === "level") pushHistory(sections);
    const arr = [...sections];
    arr[idx] = { ...arr[idx], [k]: v };
    setSections(arr);
    setDirty(true);
  };
  const addSection = (subtype = "prose", at = null) => {
    pushHistory(sections);
    const id = `sec-${Date.now()}`;
    const meta = SUBTYPE_META[subtype] || SUBTYPE_META.prose;
    const def = defaultSectionForSubtype(subtype, veSettings);
    const item = { id, level: 2, heading: def.heading || `New ${meta.label.toLowerCase()}`, type: meta.type, subtype, html: def.html, data: def.data };
    if (at === null) {
      setSections([...sections, item]);
    } else {
      const arr = [...sections];
      arr.splice(at, 0, item);
      setSections(arr);
    }
    setDirty(true);
    openSection(id);
  };
  const removeSection = (id) => {
    pushHistory(sections);
    setSections(sections.filter(s => s.id !== id));
    if (editingSectionId === id) setEditingSectionId(null);
    setDirty(true);
  };
  const toggleHidden = (id) => {
    pushHistory(sections);
    setSections(sections.map(s => s.id === id ? { ...s, hidden: !s.hidden } : s));
    setDirty(true);
  };
  const removeSectionWithToast = (id, heading) => {
    removeSection(id);
    const s = pendingDeleteRef.current;
    s.count += 1;
    const isBatch = s.count > 1;
    const tid = toast(isBatch ? `${s.count} sections deleted` : "Section deleted", {
      id: s.toastId ?? undefined,
      description: isBatch ? `Including "${heading || "Untitled section"}"` : (heading || "Untitled section"),
      duration: 6000,
      action: {
        label: isBatch ? "Undo all" : "Undo",
        onClick: () => {
          const n = pendingDeleteRef.current.count;
          const { past, future } = historyRef.current;
          const takeCount = Math.min(n, past.length);
          if (!takeCount) return;
          const popped = past.splice(past.length - takeCount, takeCount);
          const target = popped[0];
          for (let i = popped.length - 1; i >= 1; i--) {
            future.push(snap(popped[i]));
            if (future.length > 50) future.shift();
          }
          setSections(target);
          setDirty(true);
          setHistoryTick(t => t + 1);
          pendingDeleteRef.current = { toastId: null, count: 0 };
          toast(takeCount === 1 ? "Restored" : `Restored ${takeCount} sections`);
        },
      },
      onAutoClose: () => { pendingDeleteRef.current = { toastId: null, count: 0 }; },
      onDismiss: () => { pendingDeleteRef.current = { toastId: null, count: 0 }; },
    });
    s.toastId = tid;
  };

  const onDragEnd = (r) => {
    if (!r.destination) return;
    if (r.source.droppableId !== r.destination.droppableId) return;
    if (r.destination.index === r.source.index) return;
    pushHistory(sections);
    const arr = [...sections];
    const [m] = arr.splice(r.source.index, 1);
    arr.splice(r.destination.index, 0, m);
    setSections(arr);
    setDirty(true);
  };

  const doSave = async () => {
    if (readOnly) { toast.error("You no longer hold the edit lock. Take over first."); return; }
    setSaving(true);
    try {
      await onSave(sections);
      toast.success("Saved");
      setDirty(false);
      onOpenChange(false);
    } catch (e) {
      toast.error(e.response?.data?.detail || e.message || "Save failed");
    } finally { setSaving(false); }
  };

  const editingSection = sections.find(s => s.id === editingSectionId);
  const editingIndex = sections.findIndex(s => s.id === editingSectionId);

  const canUndo = historyRef.current.past.length > 0;
  const canRedo = historyRef.current.future.length > 0;
  void historyTick;

  const SubtypeMenu = ({ onPick }) => (
    <DropdownMenuContent align="start" className="w-64">
      {SECTION_GROUPS.map((g, gi) => (
        <Fragment key={g.type}>
          {gi > 0 && <DropdownMenuSeparator/>}
          <DropdownMenuLabel className="text-[10px] uppercase tracking-widest text-slate-500">{g.label}</DropdownMenuLabel>
          {g.items.map(it => {
            const Icon = it.Icon;
            return (
              <DropdownMenuItem key={it.subtype} data-testid={`pse-subtype-menu-${it.subtype}`}
                onSelect={() => onPick(it.subtype)} className="gap-3 py-2">
                <Icon className="w-4 h-4 text-sky-700 shrink-0"/>
                <div className="min-w-0">
                  <div className="text-sm font-medium">{it.label}</div>
                  <div className="text-[11px] text-slate-500 truncate">{it.hint}</div>
                </div>
              </DropdownMenuItem>
            );
          })}
        </Fragment>
      ))}
    </DropdownMenuContent>
  );

  const InsertDivider = ({ index, variant }) => (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <div data-testid={`pse-insert-${variant}-${index}`} title="Insert section here"
             className="relative h-0 z-10">
          <div className={`absolute left-0 right-0 group/insert cursor-pointer flex items-center justify-center ${
            variant === "preview" ? "-top-2 h-4 mx-6" : "-top-1 h-2"}`}>
            <div className="absolute inset-x-0 top-1/2 -translate-y-1/2 h-px bg-sky-400 opacity-0 group-hover/insert:opacity-100 transition-opacity pointer-events-none"/>
            <span className="relative z-10 opacity-0 group-hover/insert:opacity-100 transition-opacity inline-flex items-center gap-1 bg-sky-600 hover:bg-sky-500 text-white text-[10px] uppercase tracking-widest font-semibold rounded-full px-2 py-0.5 shadow">
              <Plus className="w-3 h-3"/>Insert
            </span>
          </div>
        </div>
      </DropdownMenuTrigger>
      <SubtypeMenu onPick={(sub) => addSection(sub, index)}/>
    </DropdownMenu>
  );

  return (<>
    <Dialog open={open} onOpenChange={(v) => { if (!v && dirty) { setConfirmClose(true); return; } onOpenChange(v); }}>
      <DialogContent
        className="max-w-[92vw] w-[92vw] h-[92vh] max-h-[92vh] p-0 flex flex-col overflow-hidden"
        onInteractOutside={(e) => {
          const t = e.target;
          if (t && (t.closest?.("[data-sonner-toaster]") || t.closest?.("[data-sonner-toast]"))) { e.preventDefault(); return; }
          if (confirmState.open || confirmClose) e.preventDefault();
        }}
        onEscapeKeyDown={(e) => { if (confirmState.open || confirmClose) e.preventDefault(); }}>
        <DialogHeader className="px-6 pt-5 pb-3 border-b border-slate-200 shrink-0">
          <DialogTitle className="font-heading flex items-center gap-2">
            {title}
            {dirty && <span className="text-[10px] uppercase tracking-widest bg-amber-100 text-amber-800 rounded px-1.5 py-0.5">Unsaved</span>}
            <div className="ml-auto mr-8 flex items-center gap-1">
              <button data-testid="pse-undo" onClick={undo} disabled={!canUndo} title="Undo"
                className="w-8 h-8 grid place-items-center rounded-md text-slate-500 hover:text-slate-900 hover:bg-slate-100 disabled:opacity-30 disabled:pointer-events-none transition-colors">
                <Undo2 className="w-4 h-4"/>
              </button>
              <button data-testid="pse-redo" onClick={redo} disabled={!canRedo} title="Redo"
                className="w-8 h-8 grid place-items-center rounded-md text-slate-500 hover:text-slate-900 hover:bg-slate-100 disabled:opacity-30 disabled:pointer-events-none transition-colors">
                <Redo2 className="w-4 h-4"/>
              </button>
            </div>
          </DialogTitle>
        </DialogHeader>

        <EditLockBanner
          status={lock.status}
          holder={lock.holder}
          onTakeOver={lock.takeOver}
          onClose={() => onOpenChange(false)}
        />

        <DragDropContext onDragEnd={onDragEnd}>
          <div
            className="flex-1 min-h-0 grid grid-cols-1 lg:grid-flow-col divide-x divide-slate-200"
            style={{
              gridTemplateColumns: editorUi.flip_panes
                ? `minmax(0, ${editorUi.preview_ratio}fr) minmax(0, ${100 - editorUi.preview_ratio}fr)`
                : `minmax(0, ${100 - editorUi.preview_ratio}fr) minmax(0, ${editorUi.preview_ratio}fr)`,
            }}
          >
            {/* LEFT (or RIGHT when flipped) — Controls */}
            <div data-testid="pse-editor-pane" className="flex flex-col min-h-0" style={{ order: editorUi.flip_panes ? 2 : 1 }}>
              <div className={`overflow-y-auto p-6 space-y-4 ${
                editingSection
                  ? (editorUi.editor_full_pane ? "hidden" : "flex-none h-[45%]")
                  : "flex-1"
              }`}>
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <div className="text-xs uppercase tracking-widest text-slate-500">
                      Contents ({sections.length}) <span className="text-slate-400 normal-case">— drag to reorder · click to edit</span>
                    </div>
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <button data-testid="pse-add-section" className="text-xs text-sky-700 hover:text-sky-900 inline-flex items-center gap-1">
                          <Plus className="w-3.5 h-3.5"/>Add section<ChevronDown className="w-3 h-3"/>
                        </button>
                      </DropdownMenuTrigger>
                      <SubtypeMenu onPick={(sub) => addSection(sub)}/>
                    </DropdownMenu>
                  </div>
                  <Droppable droppableId="pse-toc">
                    {(prov) => (
                      <div ref={prov.innerRef} {...prov.droppableProps} className="space-y-1">
                        {sections.map((s, i) => (
                          <Fragment key={s.id || `t-${i}`}>
                            {i === 0 && <InsertDivider index={0} variant="toc"/>}
                            <Draggable draggableId={`t-${String(s.id || i)}`} index={i}>
                              {(dp, sn) => (
                                <div ref={dp.innerRef} {...dp.draggableProps}
                                     data-testid={`pse-toc-${s.id}`}
                                     className={`group flex items-center gap-2 rounded-md px-2 py-1.5 border transition-colors ${
                                       sn.isDragging ? "border-sky-300 shadow-md bg-white"
                                         : editingSectionId === s.id ? "border-sky-300 bg-sky-50"
                                           : "border-transparent hover:border-slate-200 hover:bg-slate-50"
                                     } ${s.level === 1 ? "font-semibold" : s.level === 3 ? "pl-6" : "pl-3"} ${
                                       s.hidden ? "opacity-50" : ""}`}>
                                  <span {...dp.dragHandleProps} className="text-slate-300 hover:text-slate-600 cursor-grab active:cursor-grabbing">
                                    <GripVertical className="w-4 h-4"/>
                                  </span>
                                  <span className="text-[10px] uppercase tracking-widest text-slate-400 w-6 shrink-0">H{s.level}</span>
                                  {s.subtype && s.subtype !== "prose" && (
                                    <span className="text-[9px] uppercase tracking-widest bg-slate-100 text-slate-600 rounded px-1 py-0.5 shrink-0">
                                      {(SUBTYPE_META[s.subtype]?.label || s.subtype)}
                                    </span>
                                  )}
                                  {s.hidden && (
                                    <span className="text-[9px] uppercase tracking-widest bg-amber-100 text-amber-800 rounded px-1 py-0.5 shrink-0">Hidden</span>
                                  )}
                                  <button onClick={() => { openSection(s.id); scrollPreviewTo(s.id); }}
                                    className={`flex-1 text-left text-sm truncate ${editingSectionId === s.id ? "text-sky-800 font-semibold" : "text-slate-700 hover:text-slate-900"} ${s.hidden ? "line-through" : ""}`}>
                                    {s.heading || <span className="italic text-slate-400">Untitled section</span>}
                                  </button>
                                  <button onClick={() => toggleHidden(s.id)}
                                    data-testid={`pse-toggle-hidden-${s.id}`}
                                    title={s.hidden ? "Show section" : "Hide section"}
                                    className="opacity-0 group-hover:opacity-100 text-slate-400 hover:text-sky-700 transition-opacity">
                                    {s.hidden ? <EyeOff className="w-3.5 h-3.5"/> : <Eye className="w-3.5 h-3.5"/>}
                                  </button>
                                  <button onClick={() => openSection(s.id)}
                                    className="opacity-0 group-hover:opacity-100 text-slate-400 hover:text-sky-700 transition-opacity">
                                    <PencilLine className="w-3.5 h-3.5"/>
                                  </button>
                                  <button onClick={() => confirmDelete("Delete section?",
                                    `"${s.heading || "Untitled section"}" will be removed. You can undo.`,
                                    () => removeSectionWithToast(s.id, s.heading))}
                                    className="opacity-0 group-hover:opacity-100 text-slate-400 hover:text-red-600 transition-opacity">
                                    <Trash2 className="w-3.5 h-3.5"/>
                                  </button>
                                </div>
                              )}
                            </Draggable>
                            <InsertDivider index={i + 1} variant="toc"/>
                          </Fragment>
                        ))}
                        {prov.placeholder}
                        {sections.length === 0 && (
                          <div className="text-sm text-slate-400 italic px-2 py-6 text-center border border-dashed border-slate-200 rounded-md">
                            No sections yet. Use "Add section" above.
                          </div>
                        )}
                      </div>
                    )}
                  </Droppable>
                </div>

                {/* Workspace preferences — mirrors Blog Editor's inline UI
                   settings. Persists globally via /site-settings/blog-editor-ui. */}
                <details data-testid="pse-inline-ui-prefs" className="border border-slate-200 rounded-md bg-slate-50/50">
                  <summary className="cursor-pointer px-3 py-2 text-xs uppercase tracking-widest text-slate-500 hover:text-slate-800 list-none flex items-center justify-between">
                    <span className="inline-flex items-center gap-2">
                      <Maximize2 className="w-3.5 h-3.5"/>Workspace preferences
                    </span>
                    <ChevronDown className="w-3.5 h-3.5"/>
                  </summary>
                  <div className="px-3 py-3 space-y-3 border-t border-slate-200">
                    <div className="flex items-center gap-3">
                      <ArrowLeftRight className="w-4 h-4 text-sky-700 shrink-0"/>
                      <div className="flex-1 min-w-0">
                        <div className="text-sm text-slate-900">Flip panes</div>
                        <div className="text-xs text-slate-500">Put controls on the right, live preview on the left.</div>
                      </div>
                      <Switch data-testid="pse-flip"
                              checked={editorUi.flip_panes}
                              onCheckedChange={v => setEditorUiField({ flip_panes: v })}/>
                    </div>
                    <div className="flex items-center gap-3">
                      <Maximize2 className="w-4 h-4 text-sky-700 shrink-0"/>
                      <div className="flex-1 min-w-0">
                        <div className="text-sm text-slate-900">Editor full pane when editing</div>
                        <div className="text-xs text-slate-500">Hide the sections list while a section is open.</div>
                      </div>
                      <Switch data-testid="pse-full-pane"
                              checked={editorUi.editor_full_pane}
                              onCheckedChange={v => setEditorUiField({ editor_full_pane: v })}/>
                    </div>
                    <div>
                      <div className="flex items-baseline gap-2">
                        <div className="text-sm text-slate-900 flex-1">Preview pane width</div>
                        <div className="text-xs tabular-nums text-slate-500">{editorUi.preview_ratio}%</div>
                      </div>
                      <div className="text-xs text-slate-500 mb-2">Slide right → live preview grows · Slide left → controls grow.</div>
                      <input type="range" min="30" max="70" step="5"
                             value={editorUi.preview_ratio}
                             onChange={e => setEditorUiField({ preview_ratio: Number(e.target.value) })}
                             className="w-full"
                             data-testid="pse-preview-ratio"/>
                    </div>
                  </div>
                </details>
              </div>

              {editingSection && (
                <div ref={sectionEditorRef} data-testid="pse-inline-editor"
                     className="flex-1 min-h-0 border-t border-slate-200 bg-slate-50/50 flex flex-col">
                  <div className="px-6 py-3 border-b border-slate-200 bg-white flex items-center gap-2 shrink-0">
                    <PencilLine className="w-4 h-4 text-sky-700"/>
                    <div className="text-xs uppercase tracking-widest text-slate-500">Editing section</div>
                    <Select value={String(editingSection.level)} onValueChange={v => updateSection(editingIndex, "level", Number(v))}>
                      <SelectTrigger className="w-20 h-8 text-xs"><SelectValue/></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="1">H1</SelectItem>
                        <SelectItem value="2">H2</SelectItem>
                        <SelectItem value="3">H3</SelectItem>
                      </SelectContent>
                    </Select>
                    <Input value={editingSection.heading} onChange={e => updateSection(editingIndex, "heading", e.target.value)}
                           className="flex-1 h-8 text-sm" placeholder="Section heading"/>
                    <label className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-widest text-slate-500 mr-1">
                      <Switch data-testid={`pse-inline-visibility-${editingSection.id}`}
                              checked={!editingSection.hidden}
                              onCheckedChange={(v) => updateSection(editingIndex, "hidden", !v)}/>
                      {editingSection.hidden ? "Hidden" : "Visible"}
                    </label>
                    <button onClick={() => confirmDelete("Delete section?",
                      `"${editingSection.heading || "Untitled section"}" will be removed. You can undo.`,
                      () => removeSectionWithToast(editingSection.id, editingSection.heading))}
                      className="text-red-500 hover:text-red-700"><Trash2 className="w-4 h-4"/></button>
                    <button data-testid="pse-close-inline" onClick={() => setEditingSectionId(null)}
                      className="text-slate-400 hover:text-slate-700"><X className="w-4 h-4"/></button>
                  </div>
                  <div className="flex-1 min-h-0 overflow-y-auto p-6">
                    <SectionSubtypeEditor key={editingSection.id} section={editingSection}
                      onChange={(next) => {
                        const arr = [...sections];
                        arr[editingIndex] = next;
                        setSections(arr);
                        setDirty(true);
                      }}
                      confirmDelete={confirmDelete}/>
                  </div>
                </div>
              )}
            </div>

            {/* RIGHT — Live preview */}
            <div ref={previewPaneRef} data-testid="pse-preview-pane" className="overflow-y-auto bg-white" style={{ order: editorUi.flip_panes ? 1 : 2 }}>
              <div className="sticky top-0 z-10 bg-white/95 backdrop-blur border-b border-slate-200 px-6 py-2.5 flex items-center gap-2 text-[10px] uppercase tracking-widest text-slate-500">
                <Eye className="w-3 h-3"/> Live preview
              </div>
              <div className="px-6 md:px-10 py-12">
                <Droppable droppableId="pse-preview">
                  {(prov) => (
                    <div ref={prov.innerRef} {...prov.droppableProps}>
                      {sections.map((s, i) => {
                        const isDynamic = s?.type === "dynamic";
                        return (
                          <Fragment key={s.id || `p-${i}`}>
                            {i === 0 && <InsertDivider index={0} variant="preview"/>}
                            <Draggable draggableId={`p-${String(s.id || i)}`} index={i}>
                              {(dp, sn) => (
                                <section ref={(el) => { dp.innerRef(el); previewRefs.current[s.id] = el; }}
                                         {...dp.draggableProps}
                                         data-testid={`pse-preview-${s.id}`}
                                         onDoubleClick={() => openSection(s.id)}
                                         title={s.hidden ? "Hidden from the public site — double-click to edit" : "Double-click to edit"}
                                         className={`group relative transition-all ${s.hidden ? "opacity-40" : ""} ${
                                           sn.isDragging ? "bg-sky-50 ring-2 ring-sky-300 rounded-md shadow-lg"
                                             : editingSectionId === s.id ? "ring-2 ring-sky-400 bg-sky-50/40 rounded-md" : ""}`}>
                                  <div className={`absolute -left-8 top-2 z-10 flex flex-col gap-0.5 transition-opacity ${sn.isDragging ? "opacity-100" : "opacity-0 group-hover:opacity-100"}`}
                                       onClick={e => e.stopPropagation()}>
                                    <div {...dp.dragHandleProps}
                                         className="w-6 h-6 grid place-items-center rounded text-slate-300 hover:text-slate-700 hover:bg-slate-100 cursor-grab active:cursor-grabbing bg-white/90 border border-slate-200">
                                      <GripVertical className="w-4 h-4"/>
                                    </div>
                                    <button onClick={() => confirmDelete("Delete section?",
                                      `"${s.heading || "Untitled section"}" will be removed. You can undo.`,
                                      () => removeSectionWithToast(s.id, s.heading))}
                                      className="w-6 h-6 grid place-items-center rounded text-slate-300 hover:text-red-600 hover:bg-red-50 bg-white/90 border border-slate-200">
                                      <Trash2 className="w-4 h-4"/>
                                    </button>
                                  </div>
                                  {isDynamic ? (
                                    <SectionRenderer section={s} veSettings={veSettings}/>
                                  ) : (
                                    <div className="blog-content max-w-3xl mx-auto">
                                      <SectionRenderer section={s} veSettings={veSettings}/>
                                    </div>
                                  )}
                                </section>
                              )}
                            </Draggable>
                            <InsertDivider index={i + 1} variant="preview"/>
                          </Fragment>
                        );
                      })}
                      {prov.placeholder}
                      {sections.length === 0 && (
                        <p className="text-slate-400 italic text-center py-12">No sections yet. Click "Add section" on the left to start.</p>
                      )}
                    </div>
                  )}
                </Droppable>
              </div>
            </div>
          </div>
        </DragDropContext>

        <DialogFooter className="px-6 py-4 border-t border-slate-200 shrink-0">
          <Button variant="outline" onClick={() => { if (dirty) setConfirmClose(true); else onOpenChange(false); }}>Cancel</Button>
          <Button data-testid="pse-save" onClick={doSave} disabled={saving} className="bg-sky-700 hover:bg-sky-600">
            {saving ? "Saving…" : "Save changes"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>

    {confirmClose && createPortal(
      <div className="fixed inset-0 z-[9999] bg-black/50 grid place-items-center p-4" style={{pointerEvents:'auto'}} onClick={() => setConfirmClose(false)}>
        <div onClick={e => e.stopPropagation()} className="bg-white rounded-lg p-6 max-w-sm w-full shadow-xl">
          <div className="font-heading text-lg font-semibold text-slate-900">Unsaved changes</div>
          <div className="text-sm text-slate-600 mt-1">You have unsaved changes. Save them before closing?</div>
          <div className="mt-6 flex flex-wrap gap-2 justify-end">
            <Button variant="outline" onClick={() => setConfirmClose(false)}>Keep editing</Button>
            <Button variant="outline" className="text-red-600 border-red-300" onClick={() => { setConfirmClose(false); setDirty(false); onOpenChange(false); }}>Discard</Button>
            <Button className="bg-sky-700 hover:bg-sky-600" onClick={async () => { setConfirmClose(false); await doSave(); }}>Save & close</Button>
          </div>
        </div>
      </div>, document.body)}

    {confirmState.open && createPortal(
      <div data-testid="pse-confirm-dialog" className="fixed inset-0 z-[9999] bg-black/50 grid place-items-center p-4" style={{pointerEvents:'auto'}}
           onClick={() => setConfirmState({ open: false, title: "", body: "", onConfirm: null })}>
        <div onClick={e => e.stopPropagation()} className="bg-white rounded-lg p-6 max-w-sm w-full shadow-xl">
          <div className="font-heading text-lg font-semibold text-slate-900">{confirmState.title}</div>
          <div className="text-sm text-slate-600 mt-1">{confirmState.body}</div>
          <div className="mt-6 flex gap-2 justify-end">
            <Button variant="outline" onClick={() => setConfirmState({ open: false, title: "", body: "", onConfirm: null })}>Cancel</Button>
            <Button data-testid="pse-confirm-delete" className="bg-red-600 hover:bg-red-500 text-white"
                    onClick={() => { const fn = confirmState.onConfirm; setConfirmState({ open: false, title: "", body: "", onConfirm: null }); fn?.(); }}>Delete</Button>
          </div>
        </div>
      </div>, document.body)}
  </>);
}
